import { Component, inject, Input, OnInit, signal } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { OperationRequirement, OperationRequirementService } from "../../../_services/operation-requirement.service";
import { ArticleService } from "../../../_services";
import { ArticleTemplate } from "../../../_models";
import { StagingConfigService } from "../../../_services/staging-config.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-operation-requirements",
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <h4 class="font-semibold text-sm">Artikelbedarf</h4>
        <button class="bm-btn bm-btn-ghost bm-btn-xs" (click)="openAdd()">
          + Hinzufügen
        </button>
      </div>

      <!-- Bestehende Requirements -->
      @for (req of requirements(); track req.id) {
        <div
          class="flex items-center gap-2 bg-base-200 rounded px-3 py-1.5 text-sm cursor-pointer hover:bg-base-300 transition-colors"
          (click)="openEdit(req)"
        >
          <span
            class="bm-badge bm-badge-xs"
            [class.bm-badge-info]="req.action_type === 'STAGING'"
            [class.bm-badge-warning]="req.action_type === 'VERSAND'"
          >
            {{ req.action_type }}
          </span>
          <span class="font-medium">{{ req.quantity }}x</span>
          <span class="flex-1">{{ req.article_template_description || req.free_text }}</span>
          <button
            class="bm-btn bm-btn-ghost bm-btn-xs bm-btn-square"
            (click)="deleteRequirement(req); $event.stopPropagation()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      }

      @if (requirements().length === 0 && !showModal()) {
        <span class="text-xs text-base-content/50">Kein Artikelbedarf definiert</span>
      }

      <!-- Modal-Overlay -->
      @if (showModal()) {
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (mousedown)="onBackdropClick($event)">
          <div class="bg-base-100 rounded-lg shadow-xl w-full max-w-md mx-4 p-5 flex flex-col gap-4">
            <h3 class="font-bold text-base">
              {{ editingRequirement() ? 'Artikelbedarf bearbeiten' : 'Artikelbedarf hinzufügen' }}
            </h3>

            <!-- Typ und Menge -->
            <div class="flex gap-3">
              <div class="flex-1">
                <label class="bm-label text-xs mb-1">Typ</label>
                <select
                  class="bm-select bm-select-sm w-full"
                  [(ngModel)]="formActionType"
                  (ngModelChange)="onActionTypeChange()"
                >
                  <option value="STAGING">Staging</option>
                  <option value="VERSAND">Versand</option>
                </select>
              </div>
              <div class="w-20">
                <label class="bm-label text-xs mb-1">Menge</label>
                <input
                  type="number"
                  class="bm-input bm-input-sm w-full"
                  [(ngModel)]="formQuantity"
                  min="1"
                />
              </div>
            </div>

            <!-- Artikelvorlage -->
            <div>
              <label class="bm-label text-xs mb-1">Artikelvorlage</label>
              <div class="relative">
                @if (formActionType === "STAGING") {
                  <select
                    class="bm-select bm-select-sm w-full"
                    [(ngModel)]="formStagingTemplateId"
                    (ngModelChange)="onStagingTemplateSelect($event)"
                  >
                    <option [ngValue]="null">Artikelvorlage wählen...</option>
                    @for (tpl of stagingTemplates(); track tpl.id) {
                      <option [ngValue]="tpl.id">{{ tpl.description }}</option>
                    }
                  </select>
                } @else {
                  <input
                    type="text"
                    class="bm-input bm-input-sm w-full"
                    [(ngModel)]="formSearchText"
                    (input)="onSearchInput()"
                    placeholder="Artikelvorlage suchen oder Freitext..."
                  />
                  @if (searchResults().length > 0) {
                    <div class="absolute z-10 w-full mt-1 border border-base-300 rounded bg-base-100 shadow-lg max-h-40 overflow-y-auto">
                      @for (result of searchResults(); track result.id) {
                        <div
                          class="px-3 py-1.5 text-sm cursor-pointer hover:bg-base-200"
                          (mousedown)="selectTemplate(result)"
                        >
                          {{ result.description }}
                          @if (result.part_nr) {
                            <span class="text-base-content/50">({{ result.part_nr }})</span>
                          }
                        </div>
                      }
                    </div>
                  }
                }
              </div>
            </div>

            <!-- Aktionen -->
            <div class="flex gap-2 justify-end pt-2 border-t border-base-200">
              <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="closeModal()">Abbrechen</button>
              <button class="bm-btn bm-btn-primary bm-btn-sm" [disabled]="!canSave()" (click)="saveRequirement()">
                Speichern
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class OperationRequirementsComponent implements OnInit {
  @Input() operationId!: number;
  @Input() projectId?: number;

  private service = inject(OperationRequirementService);
  private articleService = inject(ArticleService);
  private stagingConfigService = inject(StagingConfigService);

  protected requirements = signal<OperationRequirement[]>([]);
  protected showModal = signal<boolean>(false);
  protected editingRequirement = signal<OperationRequirement | null>(null);
  protected searchResults = signal<ArticleTemplate[]>([]);
  protected selectedTemplate = signal<ArticleTemplate | null>(null);

  // Staging-Artikelvorlagen (nur die aus Staging-Configs des Projekts)
  protected stagingTemplates = signal<{ id: number; description: string }[]>([]);
  protected allTemplates = signal<ArticleTemplate[]>([]);

  // Formular-Felder
  protected formActionType: "STAGING" | "VERSAND" = "STAGING";
  protected formQuantity = 1;
  protected formSearchText = "";
  protected formStagingTemplateId: number | null = null;

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit() {
    this.requirements.set(await this.service.loadRequirements(this.operationId));
    await this.loadTemplates();
  }

  private async loadTemplates() {
    // Alle Artikelvorlagen laden (für Versand-Suche)
    const all = await firstValueFrom(this.articleService.loadArticleTemplates());
    this.allTemplates.set(all || []);

    // Staging-Artikelvorlagen aus den Staging-Configs des Projekts laden
    if (this.projectId) {
      await this.stagingConfigService.loadConfigs(this.projectId);
      const configs = this.stagingConfigService.configs();
      const templates = configs
        .filter((c) => c.enabled && c.article_template_id && c.article_template_description)
        .map((c) => ({ id: c.article_template_id!, description: c.article_template_description! }));
      // Duplikate entfernen
      const unique = [...new Map(templates.map((t) => [t.id, t])).values()];
      this.stagingTemplates.set(unique);
    }
  }

  // --- Modal öffnen/schließen ---

  openAdd() {
    this.resetForm();
    this.editingRequirement.set(null);
    this.showModal.set(true);
  }

  openEdit(req: OperationRequirement) {
    this.resetForm();
    this.editingRequirement.set(req);
    this.formActionType = req.action_type;
    this.formQuantity = req.quantity;

    if (req.action_type === "STAGING" && req.article_template_id) {
      this.formStagingTemplateId = req.article_template_id;
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
    } else if (req.article_template_id) {
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
      this.formSearchText = req.article_template_description || "";
    } else {
      this.formSearchText = req.free_text || "";
    }

    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
    this.editingRequirement.set(null);
    this.resetForm();
  }

  onBackdropClick(event: MouseEvent) {
    // Nur schließen wenn direkt auf den Backdrop geklickt wird
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  // --- Formular-Logik ---

  onActionTypeChange() {
    const current = this.selectedTemplate();
    this.searchResults.set([]);

    if (current) {
      if (this.formActionType === "STAGING") {
        // Versand → Staging: Vorlage beibehalten wenn sie eine gültige Staging-Vorlage ist
        const validStaging = this.stagingTemplates().find((t) => t.id === current.id);
        if (validStaging) {
          this.formStagingTemplateId = validStaging.id;
          this.formSearchText = "";
          return;
        }
      } else {
        // Staging → Versand: Vorlage übernehmen ins Suchfeld
        this.formSearchText = current.description || "";
        this.formStagingTemplateId = null;
        return;
      }
    }

    // Auswahl zurücksetzen wenn kein Match
    this.selectedTemplate.set(null);
    this.formStagingTemplateId = null;
    this.formSearchText = "";
  }

  onStagingTemplateSelect(templateId: number | null) {
    if (!templateId) {
      this.selectedTemplate.set(null);
      return;
    }
    const tpl = this.stagingTemplates().find((t) => t.id === templateId);
    if (tpl) {
      this.selectedTemplate.set({ id: tpl.id, description: tpl.description } as ArticleTemplate);
    }
  }

  onSearchInput() {
    this.selectedTemplate.set(null);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);

    if (this.formSearchText.trim().length < 2) {
      this.searchResults.set([]);
      return;
    }

    this.searchDebounce = setTimeout(() => this.performSearch(), 300);
  }

  private performSearch() {
    const query = this.formSearchText.toLowerCase();
    const results = this.allTemplates().filter(
      (t) => t.description?.toLowerCase().includes(query) || t.part_nr?.toLowerCase().includes(query),
    );
    this.searchResults.set(results.slice(0, 15));
  }

  selectTemplate(template: ArticleTemplate) {
    this.selectedTemplate.set(template);
    this.formSearchText = template.description;
    this.searchResults.set([]);
  }

  canSave(): boolean {
    if (this.formQuantity <= 0) return false;
    if (this.formActionType === "STAGING") {
      return this.selectedTemplate() !== null;
    }
    return this.selectedTemplate() !== null || this.formSearchText.trim().length > 0;
  }

  async saveRequirement() {
    const template = this.selectedTemplate();
    const data: Partial<OperationRequirement> = {
      action_type: this.formActionType,
      quantity: this.formQuantity,
    };

    if (template) {
      data.article_template_id = template.id;
      data.free_text = null as any;
    } else {
      data.free_text = this.formSearchText.trim();
      data.article_template_id = null as any;
    }

    const editing = this.editingRequirement();
    if (editing) {
      // Bestehendes Requirement aktualisieren
      const updated = await this.service.updateRequirement(editing.id, data);
      if (updated) {
        this.requirements.update((reqs) => reqs.map((r) => (r.id === editing.id ? updated : r)));
        this.closeModal();
      }
    } else {
      // Neues Requirement anlegen
      const result = await this.service.createRequirement(this.operationId, data);
      if (result) {
        this.requirements.update((reqs) => [...reqs, result]);
        this.closeModal();
      }
    }
  }

  async deleteRequirement(req: OperationRequirement) {
    const label = req.article_template_description || req.free_text || 'Unbekannt';
    if (!confirm(`"${req.quantity}x ${label}" wirklich löschen?`)) return;
    const success = await this.service.deleteRequirement(req.id);
    if (success) {
      this.requirements.update((reqs) => reqs.filter((r) => r.id !== req.id));
    }
  }

  private resetForm() {
    this.formActionType = "STAGING";
    this.formQuantity = 1;
    this.formSearchText = "";
    this.selectedTemplate.set(null);
    this.formStagingTemplateId = null;
    this.searchResults.set([]);
  }
}
